import styled from '@emotion/styled'
import gql from 'graphql-tag'
import React, { useState } from 'react'
import { useMutation } from '../../../api/graphql'

const UPDATE_MUTATION = gql`
  mutation SocialPostUpdate($id: ID!, $caption: String, $scheduledFor: ISO8601DateTime) {
    socialPostUpdate(id: $id, caption: $caption, scheduledFor: $scheduledFor) {
      socialPost {
        id
        caption
        scheduledFor
        status
      }
      errors {
        attribute
        message
      }
    }
  }
`

type Props = {
  post: {
    id: string
    caption: string
    scheduledFor: string | null
    platform: string
    eventTitle: string
  }
  onClose: () => void
  onSaved: () => void
}

const SocialPostEditModal: React.FC<Props> = ({ post, onClose, onSaved }) => {
  const [caption, setCaption] = useState(post.caption)
  const [scheduledFor, setScheduledFor] = useState(
    post.scheduledFor ? new Date(post.scheduledFor).toISOString().slice(0, 16) : '',
  )
  const [updatePost, { loading }] = useMutation(UPDATE_MUTATION)

  const handleSave = async () => {
    await updatePost({
      variables: {
        id: post.id,
        caption,
        scheduledFor: scheduledFor ? new Date(scheduledFor).toISOString() : null,
      },
    })
    onSaved()
  }

  const platformLabel = post.platform === 'facebook_page' ? 'Facebook' : 'Instagram'

  return (
    <Overlay>
      <Modal>
        <ModalHeader>
          <ModalTitle>Edit Post</ModalTitle>
          <ModalSubtitle>
            {platformLabel} post for &ldquo;{post.eventTitle}&rdquo;
          </ModalSubtitle>
        </ModalHeader>

        <ModalBody>
          <Label>Caption</Label>
          <CaptionTextarea value={caption} onChange={e => setCaption(e.target.value)} rows={6} />

          <Label>Schedule</Label>
          <DateInput type="datetime-local" value={scheduledFor} onChange={e => setScheduledFor(e.target.value)} />
        </ModalBody>

        <ModalFooter>
          <CancelButton onClick={onClose}>Cancel</CancelButton>
          <SaveButton onClick={handleSave} disabled={loading}>
            {loading ? 'Saving...' : 'Save Changes'}
          </SaveButton>
        </ModalFooter>
      </Modal>
    </Overlay>
  )
}

export default SocialPostEditModal

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 24px;
`

const Modal = styled.div`
  background: white;
  border-radius: 16px;
  max-width: 500px;
  width: 100%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
`

const ModalHeader = styled.div`
  padding: 24px 24px 16px;
  border-bottom: 1px solid #e5e7eb;
`

const ModalTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  color: #102638;
  margin: 0 0 4px;
`

const ModalSubtitle = styled.p`
  font-size: 13px;
  color: #6b7280;
  margin: 0;
`

const ModalBody = styled.div`
  padding: 20px 24px;
`

const Label = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 6px;
  margin-top: 16px;

  &:first-of-type {
    margin-top: 0;
  }
`

const CaptionTextarea = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 13px;
  font-family: inherit;
  line-height: 1.5;
  resize: vertical;
  color: #374151;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`

const DateInput = styled.input`
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  color: #374151;
`

const ModalFooter = styled.div`
  padding: 16px 24px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`

const CancelButton = styled.button`
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  color: #6b7280;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;

  &:hover {
    background: #f9fafb;
  }
`

const SaveButton = styled.button`
  padding: 8px 20px;
  font-size: 14px;
  font-weight: 600;
  color: white;
  background: #1e3a5f;
  border: none;
  border-radius: 8px;
  cursor: pointer;

  &:hover {
    background: #2d4a6f;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`
